import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  X,
  Loader2,
  Map as MapIcon,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { api } from "../../api";
import { qk } from "../../hooks/queries";
import { ApiError } from "../../api/client";
import { useUiStore } from "../../stores/uiStore";
import { useLayerStore } from "../../stores/layerStore";
import { withUnit } from "../../lib/layerRegistry";
import { LAYER_RADIO_CLASS } from "../../lib/layerToggleClass";
import {
  useDataSources,
  inactiveSources,
  useServerDataSources,
} from "../../stores/dataSources";
import {
  useFieldCovariates,
  useFieldLayerList,
  useSampleEvents,
  useDesignateCurrentEvent,
} from "../../hooks/queries";
import {
  pinState,
  eventStatusLabel,
  fmtEventDate,
} from "../../lib/sampleEvents";
import type { DashboardField, FieldCovariate } from "../../types/contract";
import { schoolProps } from "../../lib/schoolProperties";

// "Map soil properties" per-field panel (Front-End.md §A6). DEFAULT = use ALL field data
// (the full covariate stack) + the trained AI models. Options:
// uncheck "Use all field data" -> pick covariate layers from the field's catalog
// "Pick weights myself" -> set a weight per picked layer (implies picking)
// "Sample event" selector -> choose WHICH points/zones sampling campaign feeds
// the run (defaults to the latest; events come from the Soil samples flow)
// Runs dispatch model:run (M1) with {properties, layers?, weights?, sample_event_id?}.

// This panel is the ML school. Buffer pH is not a trained target, so it is not offered.
const DEFAULT_PROPS = schoolProps("ml");
// Everything M1 can model (the 8"-normalized bag). Ca + Mg are what the fertility
// engine's cation-balance (BCSR) correction needs — modeling them unlocks that option.
const MODELABLE_PROPS: { key: string; label: string }[] = [
  { key: "ph", label: "pH" },
  { key: "om", label: "OM" },
  { key: "cec", label: "CEC" },
  { key: "p", label: "P" },
  { key: "k", label: "K" },
  { key: "ca", label: "Ca" },
  { key: "mg", label: "Mg" },
  { key: "s", label: "S" },
  { key: "zn", label: "Zn" },
  { key: "mn", label: "Mn" },
  { key: "b", label: "B" },
];
const PICKER_CAP = 100; // rows shown at once — search narrows the rest

// Saved (already-produced) soil-property maps in the field's layer manifest — modeled
// output rows use the `ml-` id convention (mock `ml-map`; live `ml-<property>` per the
// manifest request to BE-light).
const isSavedMap = (l: { id?: string }) => (l.id ?? "").startsWith("ml-");

// Last dispatched mapping job per field, persisted so its outcome is visible LATER —
// a failed run must not vanish just because the tab wasn't open when the job died.
const LAST_JOB_KEY = "sm_soilmap_last_job";
function loadLastJob(fieldId: string): string | null {
  try {
    return (
      (
        JSON.parse(localStorage.getItem(LAST_JOB_KEY) ?? "{}") as Record<
          string,
          string
        >
      )[fieldId] ?? null
    );
  } catch {
    return null;
  }
}
function saveLastJob(fieldId: string, jobId: string) {
  try {
    const m = JSON.parse(localStorage.getItem(LAST_JOB_KEY) ?? "{}") as Record<
      string,
      string
    >;
    m[fieldId] = jobId;
    localStorage.setItem(LAST_JOB_KEY, JSON.stringify(m));
  } catch {
    /* private mode / quota — status line just won't persist */
  }
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : "Something went wrong.";
}

export function MapSoilPropsOptions({ field }: { field: DashboardField }) {
  const openMap = useUiStore((s) => s.openMap);
  const { data: covs, isLoading } = useFieldCovariates(field.field_id);
  const { data: listed } = useFieldLayerList(field.field_id);
  const manifest = listed?.document.layers;
  const layerUi = useLayerStore((s) => s.byId);
  const setVisible = useLayerStore((s) => s.setVisible);
  const savedMaps = useMemo(
    () => (manifest ?? []).filter(isSavedMap),
    [manifest],
  );
  // Sample events (zones/points campaigns from the Soil samples flow) — newest first
  // the run uses the selected one (default = latest).
  const { data: events } = useSampleEvents(field.field_id);
  const designate = useDesignateCurrentEvent();
  const pin = useMemo(() => pinState(events), [events]);
  const sortedEvents = useMemo(
    () =>
      [...(events ?? [])].sort((a, b) =>
        b.created_at.localeCompare(a.created_at),
      ),
    [events],
  );

  const [props, setProps] = useState<Set<string>>(new Set(DEFAULT_PROPS));
  const [useAll, setUseAll] = useState(true);
  const [pickWeights, setPickWeights] = useState(false);
  const [eventId, setEventId] = useState<string | null>(null); // null = latest (default)
  // Default to the NEWEST campaign by sample_date — "mapping should always use the most recen soil
  // samples").
  const effectiveEventId =
    eventId ??
    pin.newest?.event_id ??
    pin.current?.event_id ??
    sortedEvents[0]?.event_id ??
    null;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [noteErr, setNoteErr] = useState(false);
  const [lastJobId, setLastJobId] = useState<string | null>(() =>
    loadLastJob(field.field_id),
  );
  const qc = useQueryClient();

  // Last mapping run (GET /jobs/{id}). Poll while it is still in the queue.
  const { data: lastJob } = useQuery({
    queryKey: ["job", lastJobId ?? ""],
    queryFn: () => api.fetchJob(lastJobId as string),
    enabled: !!lastJobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "waiting" || s === "in_progress" || s === "blocked"
        ? 5000
        : false;
    },
  });
  const lastDone = lastJob?.status === "complete";
  useEffect(() => {
    // finished while we were watching -> refresh the manifest so Saved maps shows it
    if (lastDone)
      void qc.invalidateQueries({ queryKey: qk.layers(field.field_id) });
  }, [lastDone, qc, field.field_id]);

  // Inactive data sources (Add Field Data slide-out): their layers are EXCLUDED from the
  // run, and the trained-AI path is off the table — partial data REQUIRES manual weights.
  const overrides = useDataSources((s) => s.overrides);
  useServerDataSources(field); // account-level flags drive the forced-manual rule too
  const inactive = useMemo(
    () => inactiveSources(overrides, field),
    [overrides, field],
  );
  const inactiveCats = useMemo(
    () => new Set(inactive.map((d) => d.category).filter(Boolean)),
    [inactive],
  );
  const forcedManual = inactive.length > 0;

  // HARD RULE: any inactive data source => the AI model can't run on partial data, so "Pick weights
  // myself" is forced ON (and use-all off -> pick your layers).
  useEffect(() => {
    if (forcedManual) {
      setPickWeights(true);
      setUseAll(false);
    }
  }, [forcedManual]);

  const usable = useMemo(
    () =>
      (covs ?? []).filter((c) => c.has_values && !inactiveCats.has(c.category)),
    [covs, inactiveCats],
  );
  // Show BOTH numbers: the catalog is bigger than the usable set (no-data columns — e.g.
  // machine years without ops — can't feed a mapping run), and one bare number reads as a bug.
  const layerCountLabel = isLoading
    ? "all layers"
    : `${usable.length.toLocaleString()} of ${(covs ?? []).length.toLocaleString()} layers have data`;

  // Only an EXPLICIT `kind === 'categorical'` is excluded, so a catalog that predates the field
  // stays fully pickable rather than silently emptying.
  const pickable = useMemo(
    () => usable.filter((c) => c.kind !== "categorical"),
    [usable],
  );

  const matchesQuery = (c: FieldCovariate, q: string) =>
    !q ||
    (c.title ?? "").toLowerCase().includes(q) ||
    (c.key ?? "").toLowerCase().includes(q) ||
    (c.family ?? "").toLowerCase().includes(q);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pickable.filter((c) => matchesQuery(c, q));
  }, [pickable, query]);

  // How many categoricals the CURRENT search would have matched — so a user hunting for "map unit" gets an
  // explanation instead of an empty list.
  const hiddenCategoricals = useMemo(() => {
    const q = query.trim().toLowerCase();
    return usable.filter((c) => c.kind === "categorical" && matchesQuery(c, q))
      .length;
  }, [usable, query]);

  const togglePick = (key: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  // Manual weights only make sense for a picked subset — checking it switches off "use all" so the
  // picker appears — "after checking them off they get to set weights".
  const onPickWeights = (on: boolean) => {
    setPickWeights(on);
    if (on) setUseAll(false);
  };
  const onUseAll = (on: boolean) => {
    setUseAll(on);
    if (on) setPickWeights(false); // all data -> the trained AI models weigh it
  };

  // derive from `pickable`, not `usable`: a categorical can then never reach the dispatch even if it is
  // somehow in `picked` (stale state, a future restore path) — the run would fail terminally
  const pickedList = useMemo(
    () => pickable.filter((c) => picked.has(c.key)),
    [pickable, picked],
  );
  const needsPicks = !useAll && picked.size === 0;

  async function run() {
    setBusy(true);
    setNote(null);
    setNoteErr(false);
    try {
      const layers = useAll
        ? usable.map((c) => c.key)
        : pickedList.map((c) => c.key);
      const w = pickWeights
        ? pickedList.map((c) => weights[c.key] ?? 1)
        : undefined;
      // model:run (M1) — the chosen soil PROPERTIES; default full stack + trained AI
      // subset/weights when picked; sample_event_id says which campaign feeds the run.
      const properties = MODELABLE_PROPS.filter((p) => props.has(p.key)).map(
        (p) => p.key,
      );
      const job = await api.runModel(field.field_id, properties, {
        ...(useAll ? {} : { layers, weights: w }),
        ...(effectiveEventId ? { sample_event_id: effectiveEventId } : {}),
      });
      setNote(
        "Soil-property mapping dispatched — when ready, the maps appear under Saved maps here and in the map's layer list.",
      );
      if (job?.job_id) {
        saveLastJob(field.field_id, job.job_id);
        setLastJobId(job.job_id);
      }
    } catch (e) {
      setNote(errMsg(e));
      setNoteErr(true);
    } finally {
      setBusy(false);
    }
  }

  const check = (
    checked: boolean,
    onChange: (v: boolean) => void,
    label: string,
    hint?: string,
    disabled = false,
  ) => (
    <label
      className={`flex items-start gap-1.5 text-[11px] text-fg ${disabled ? "opacity-70" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span>
        {label}
        {hint && <span className="ml-1 text-faint">{hint}</span>}
      </span>
    </label>
  );

  const toggleProp = (key: string) =>
    setProps((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const cationReady = props.has("ca") && props.has("mg");

  return (
    <div className="flex flex-col gap-2">
      {/* which soil PROPERTIES to model — Ca + Mg unlock the fertility cation-balance rec */}
      <div className="rounded border border-line bg-canvas/60 p-2">
        <div className="mb-1 text-[11px] font-medium">Properties to map</div>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {MODELABLE_PROPS.map((p) => (
            <label
              key={p.key}
              className="flex cursor-pointer items-center gap-1 text-[11px] text-fg"
            >
              <input
                type="checkbox"
                checked={props.has(p.key)}
                onChange={() => toggleProp(p.key)}
                aria-label={p.label}
              />
              {p.label}
            </label>
          ))}
        </div>
        <div className="mt-1 text-[10px] text-faint">
          {cationReady
            ? "Ca + Mg selected — the fertility cation-balance (BCSR) rec will be available once these maps are produced."
            : "Add Ca + Mg to enable the fertility cation-balance (BCSR) rec."}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        {check(
          useAll,
          onUseAll,
          `Use all field data (${layerCountLabel})`,
          forcedManual
            ? "unavailable — some data sources are inactive"
            : "instead of picking — the trained AI models weigh them",
          forcedManual,
        )}
        {check(
          pickWeights,
          onPickWeights,
          "Pick weights myself",
          forcedManual
            ? "REQUIRED — inactive data sources mean the AI model can't run on partial data"
            : "instead of using AI",
          forcedManual,
        )}
      </div>

      {/* which points/zones sample event feeds the run (from the Soil samples flow) */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-muted">Sample event:</span>
        {sortedEvents.length > 0 ? (
          <select
            value={effectiveEventId ?? ""}
            onChange={(e) => setEventId(e.target.value)}
            aria-label="Sample event"
            className="rounded border border-line bg-surface px-1.5 py-1 text-[11px] text-fg outline-none"
          >
            {sortedEvents.map((ev, i) => (
              <option key={ev.event_id} value={ev.event_id}>
                {ev.name} — {ev.count} {ev.kind}
                {ev.has_results ? " · results in" : " · no results yet"}
                {eventStatusLabel(ev, i)}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-[11px] text-faint">
            none yet — add one under the “Soil samples” checkbox first
          </span>
        )}
      </div>

      {designate.isError && (
        <p role="alert" className="text-[11px] text-red-400">
          Could not change the current samples — {errMsg(designate.error)}
        </p>
      )}
      {pin.overriding && (
        <div
          role="status"
          className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px]"
        >
          <span className="text-fg">
            Still using <span className="font-medium">{pin.current!.name}</span>
            {pin.current!.sample_date
              ? ` (${fmtEventDate(pin.current!.sample_date)})`
              : ""}{" "}
            — newer samples exist and are being treated as old.
          </span>
          <button
            className="ml-2 rounded border border-line px-1.5 py-0.5 text-[10px] text-muted hover:bg-hover hover:text-fg disabled:opacity-50"
            disabled={designate.isPending}
            onClick={() =>
              designate.mutate({ fieldId: field.field_id, eventId: null })
            }
          >
            {designate.isPending ? "Updating…" : "Use newest"}
          </button>
        </div>
      )}

      {/* layer picker — shown when not using all data */}
      {!useAll && (
        <div className="rounded border border-line bg-canvas/60 p-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium">
              Pick data layers ({picked.size.toLocaleString()} selected)
            </span>
            {picked.size > 0 && (
              <button
                className="text-[10px] text-faint hover:text-fg"
                onClick={() => setPicked(new Set())}
              >
                clear
              </button>
            )}
          </div>
          <div className="mb-1 flex items-center gap-1.5 rounded border border-line bg-surface px-1.5">
            <Search size={11} className="text-faint" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search layers…"
              className="w-full bg-transparent py-1 text-[11px] outline-none placeholder:text-faint"
            />
            {query && (
              <button onClick={() => setQuery("")} aria-label="Clear search">
                <X size={10} className="text-faint" />
              </button>
            )}
          </div>
          {isLoading ? (
            <p className="py-2 text-center text-[11px] text-muted">
              Loading the field's data layers…
            </p>
          ) : (
            <>
              {hiddenCategoricals > 0 && (
                <p className="mb-1 text-[10px] text-faint">
                  {hiddenCategoricals} categorical layer
                  {hiddenCategoricals === 1 ? "" : "s"} (soil map unit,
                  taxonomy, series…) {hiddenCategoricals === 1 ? "is" : "are"}{" "}
                  not listed — they have no numeric magnitude to weight into a
                  continuous surface. Use{" "}
                  <span className="text-fg">Multi-Input, Relative Zones</span>{" "}
                  to map from those.
                </p>
              )}
              <ul className="max-h-48 space-y-0.5 overflow-auto">
                {shown.slice(0, PICKER_CAP).map((c) => (
                  <li
                    key={c.key}
                    className="flex items-center justify-between gap-2"
                  >
                    <label className="flex min-w-0 cursor-pointer items-center gap-1.5 text-[11px]">
                      <input
                        type="checkbox"
                        checked={picked.has(c.key)}
                        onChange={() => togglePick(c.key)}
                        aria-label={c.title}
                      />
                      <span className="truncate">{c.title}</span>
                      <span className="shrink-0 text-[9px] text-faint">
                        {c.category}
                      </span>
                    </label>
                    {pickWeights && picked.has(c.key) && (
                      <span className="flex shrink-0 items-center gap-1 text-[10px] text-faint">
                        weight
                        <input
                          type="number"
                          min={0}
                          max={10}
                          step={0.1}
                          value={weights[c.key] ?? 1}
                          onChange={(e) =>
                            setWeights((m) => ({
                              ...m,
                              [c.key]: Number(e.target.value),
                            }))
                          }
                          aria-label={`Weight for ${c.title}`}
                          className="w-14 rounded border border-line bg-surface px-1 py-0.5 text-[11px] text-fg outline-none"
                        />
                      </span>
                    )}
                  </li>
                ))}
                {shown.length > PICKER_CAP && (
                  <li className="py-0.5 text-[10px] text-faint">
                    Showing {PICKER_CAP} of {shown.length.toLocaleString()} —
                    search to narrow.
                  </li>
                )}
                {shown.length === 0 && (
                  <li className="py-1 text-[11px] text-muted">
                    No layers match.
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Saved (already-produced) soil-property maps — checkbox = visible on the map view */}
      {savedMaps.length > 0 && (
        <div className="rounded border border-line bg-canvas/60 p-2">
          <span className="mb-1 block text-[11px] font-medium">Saved maps</span>
          <ul className="space-y-0.5">
            {savedMaps.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between gap-2"
              >
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
                  <input
                    type="checkbox"
                    className={LAYER_RADIO_CLASS}
                    checked={Boolean(
                      layerUi[l.id]?.visible ?? l.default_visible,
                    )}
                    onChange={(e) => setVisible(l.id, e.target.checked)}
                    aria-label={withUnit(l.title, l.unit)}
                  />
                  <span className="truncate">{withUnit(l.title, l.unit)}</span>
                  {"is_stale" in l && l.is_stale ? (
                    <span className="rounded bg-amber-400/15 px-1 text-[9px] text-amber-300">
                      stale
                    </span>
                  ) : null}
                </label>
                <button
                  className="shrink-0 rounded border border-line px-1.5 py-0.5 text-[10px] text-muted hover:bg-hover hover:text-fg"
                  onClick={() => {
                    setVisible(l.id, true);
                    openMap(field.field_id);
                  }}
                >
                  show
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          disabled={busy || needsPicks || props.size === 0}
          onClick={() => void run()}
          className="flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-xs font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          Map soil properties
        </button>
        <button
          onClick={() => openMap(field.field_id)}
          className="flex items-center gap-1 rounded border border-line px-2 py-1.5 text-[11px] text-muted hover:bg-hover hover:text-fg"
        >
          <MapIcon size={12} /> View on map
        </button>
        {needsPicks && (
          <span className="text-[10px] text-faint">
            pick at least one layer (or use all field data)
          </span>
        )}
        {props.size === 0 && (
          <span className="text-[10px] text-faint">
            pick at least one property to map
          </span>
        )}
      </div>

      {note && (
        <span
          className={`text-[11px] ${noteErr ? "text-red-400" : "text-muted"}`}
        >
          {note}
        </span>
      )}

      {/* durable last-run status — survives tab closes; failures stay visible + diagnosable */}
      {lastJob && (
        <span className="flex items-center gap-1.5 text-[11px]">
          {(lastJob.status === "waiting" ||
            lastJob.status === "in_progress" ||
            lastJob.status === "blocked") && (
            <>
              <Loader2 size={11} className="animate-spin text-accent" />
              <span className="text-muted">
                Last mapping run: {lastJob.status}
              </span>
            </>
          )}
          {lastJob.status === "complete" && (
            <>
              <CheckCircle2 size={11} className="text-emerald-400" />
              <span className="text-muted">
                Last mapping run: completed — see Saved maps above / the map's
                layer list.
              </span>
            </>
          )}
          {(lastJob.status === "failed" || lastJob.status === "canceled") && (
            <>
              <XCircle size={11} className="shrink-0 text-red-400" />
              <span className="text-red-400">
                Last mapping run {lastJob.status}
                {lastJob.error ? `: ${lastJob.error}` : ""} — fix the cause and
                re-run.
              </span>
            </>
          )}
        </span>
      )}
    </div>
  );
}
